/*
 * SectionNavigationTab Specification
 * @module SectionNavigation
 * @flow
 */

import React from 'react';
import { NavLink } from 'react-router-dom';
import styled, { css } from 'styled-components/macro';
import { ifProp } from 'styled-tools';
import { palette } from 'styled-theme';
import { transparentize } from 'polished';
import * as R from 'ramda';

export type SectionNavigationTabProps = $Shape<DeviceQuery> & {
  icon?: React$Node,
  label: string,
  link: string,
  palette?: string,
  tabRef: React$ElementRef<*>,
  checkIsActive: () => {},
};

const tabColor = R.pipe(
  palette('grayscale', 0),
  transparentize(0.3),
);

const SectionNavigationTabWrapper = styled(NavLink).attrs(() => ({
  className: 'navigation-tab',
  role: 'tab',
}))`
  display: inline-flex;
  align-items: center;
  color: ${tabColor};
  cursor: pointer;
  font-size: 14px;
  letter-spacing: 0.5px;
  pointer-events: auto;
  text-align: center;
  transition: all 250ms ease-out;

  ${ifProp(
    { device: 'desktop' },
    css`
      padding: 9px 15px;

      & + & {
        margin-left: 10px;
      }

      ${ifProp(
        { locale: 'en' },
        css`
          & > span {
            display: block;
            white-space: normal;
            max-width: 130px;
          }
        `,
      )}
    `,
    css`
      padding: 7px;

      & + & {
        margin-left: 20px;
      }
    `,
  )}

  &:hover {
    color: ${palette('grayscale', 0)};
  }

  &.navigation-tab__active {
    color: ${palette('grayscale', 0)};
    cursor: default;
    border-bottom: 2px solid ${palette(1)};
    pointer-events: none;
  }
`;

const SectionNavigationTabIcon = styled.div`
  margin-right: 5px;
  color: ${palette(1)};
`;

/**
 * SectionNavigationTab React-component
 * @memberof Tabs
 * @param {Object} props - properties
 * @param {$PropertyType<DeviceQuery, 'device'>} [props.device] - device mode
 * @param {React.Node<*>} [props.icon] - icon
 * @param {string} [props.label] - label
 * @param {string} [props.link] - link
 * @param {string} [props.palette] - palette
 * @param {React$ElementRef<*>} [props.tabRef] - element reference
 * @param {Function} [props.checkIsActive] - check tab is active
 * @example <caption>Simple usage</caption>
 * <SectionNavigationTab icon={<HomeIcon />} label="Home" link="/home" />
 * @returns {React.Node<*>} React-component
 */
export const SectionNavigationTab = ({
  device,
  icon,
  label,
  link,
  palette,
  tabRef,
  locale,
  checkIsActive,
}: SectionNavigationTabProps) => (
  <SectionNavigationTabWrapper
    activeClassName="navigation-tab__active"
    locale={locale}
    device={device}
    palette={palette}
    ref={tabRef}
    to={link}
    isActive={checkIsActive}
    testid="navigation-tab"
  >
    <SectionNavigationTabIcon palette={palette}>
      {icon}
    </SectionNavigationTabIcon>
    <span>{label}</span>
  </SectionNavigationTabWrapper>
);

// $FlowIssue
SectionNavigationTab.defaultProps = {
  palette: 'primary',
};
